import { z } from 'zod';

export const COASTER_PROJECT_SCHEMA_VERSION = 1 as const;

export const StableIdSchema = z
  .string()
  .min(1)
  .max(96)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/, 'Must be a stable identifier');

const FiniteNumberSchema = z.number().finite();
const NonNegativeNumberSchema = FiniteNumberSchema.min(0);
const PositiveNumberSchema = FiniteNumberSchema.positive();
const IsoDateSchema = z.iso.datetime({ offset: true });

export const Vec3Schema = z.object({
  x: FiniteNumberSchema,
  y: FiniteNumberSchema,
  z: FiniteNumberSchema,
});

export const ColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Expected a six-digit hex color');

const ElementReferenceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('project'), id: StableIdSchema }),
  z.object({ kind: z.literal('track_node'), id: StableIdSchema }),
  z.object({ kind: z.literal('track_section'), id: StableIdSchema }),
  z.object({ kind: z.literal('support'), id: StableIdSchema }),
  z.object({ kind: z.literal('scenery'), id: StableIdSchema }),
  z.object({ kind: z.literal('highlight'), id: StableIdSchema }),
]);

export const TrackBehaviorSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('normal') }),
  z.object({
    kind: z.literal('station'),
    holdSeconds: NonNegativeNumberSchema.max(300),
    dispatchSpeedMps: NonNegativeNumberSchema.max(20),
  }),
  z.object({
    kind: z.literal('lift'),
    targetSpeedMps: PositiveNumberSchema.max(20),
  }),
  z.object({
    kind: z.literal('launch'),
    targetSpeedMps: PositiveNumberSchema.max(200),
    accelerationMps2: PositiveNumberSchema.max(100),
  }),
  z.object({
    kind: z.literal('brake'),
    targetSpeedMps: NonNegativeNumberSchema.max(200),
    decelerationMps2: PositiveNumberSchema.max(100),
  }),
  z.object({ kind: z.literal('photo') }),
]);

export const TrackNodeSchema = z.object({
  id: StableIdSchema,
  position: Vec3Schema,
  bankDegrees: FiniteNumberSchema.min(-180).max(180),
});

export const TrackSectionSchema = z.object({
  id: StableIdSchema,
  fromNodeId: StableIdSchema,
  toNodeId: StableIdSchema,
  behavior: TrackBehaviorSchema,
});

const ValidationIssueSchema = z.object({
  id: StableIdSchema,
  severity: z.enum(['info', 'warning', 'severe', 'blocker']),
  code: z.string().min(1).max(80),
  message: z.string().min(1).max(500),
  element: ElementReferenceSchema,
  metric: z
    .object({
      name: z.string().min(1).max(80),
      value: FiniteNumberSchema,
      unit: z.string().min(1).max(24),
    })
    .optional(),
});

const ProjectMetadataSchema = z.object({
  title: z.string().min(1).max(80),
  description: z.string().max(500),
  createdAt: IsoDateSchema,
  updatedAt: IsoDateSchema,
  creator: z.object({
    kind: z.enum(['local', 'buncha']),
    id: StableIdSchema.nullable(),
    displayName: z.string().min(1).max(80),
  }),
  tags: z.array(z.string().min(1).max(32)).max(10),
});

const PlotSchema = z.object({
  shape: z.literal('rectangle'),
  sizeMeters: z.object({
    x: PositiveNumberSchema.max(2_000),
    z: PositiveNumberSchema.max(2_000),
  }),
  origin: Vec3Schema,
  groundElevationMeters: FiniteNumberSchema,
});

const TrainSchema = z.object({
  familyId: StableIdSchema,
  styleId: StableIdSchema,
  carCount: z.number().int().min(1).max(64),
  carSpacingMeters: PositiveNumberSchema.max(20),
  emptyMassKg: PositiveNumberSchema.max(1_000_000),
  capacityPerCar: z.number().int().min(1).max(16),
});

const AppearanceSchema = z.object({
  environmentThemeId: StableIdSchema,
  trackColor: ColorSchema,
  supportColor: ColorSchema,
  trainColor: ColorSchema,
  stationColor: ColorSchema,
  trackMaterialId: StableIdSchema,
  supportMaterialId: StableIdSchema,
  trainMaterialId: StableIdSchema,
});

const ManualSupportSchema = z.object({
  id: StableIdSchema,
  sectionId: StableIdSchema,
  distanceAlongSection: NonNegativeNumberSchema.max(1),
  groundAnchor: Vec3Schema,
  hidden: z.boolean(),
});

const SceneryItemSchema = z.object({
  id: StableIdSchema,
  assetId: StableIdSchema,
  position: Vec3Schema,
  rotationDegrees: Vec3Schema,
  scale: z.object({
    x: PositiveNumberSchema.max(100),
    y: PositiveNumberSchema.max(100),
    z: PositiveNumberSchema.max(100),
  }),
  colorVariant: StableIdSchema.nullable(),
});

const HighlightSchema = z.object({
  id: StableIdSchema,
  kind: z.enum(['creator', 'photo', 'automatic']),
  sectionId: StableIdSchema,
  distanceAlongSection: NonNegativeNumberSchema.max(1),
  label: z.string().min(1).max(60),
});

const HeroCameraSchema = z.object({
  position: Vec3Schema,
  target: Vec3Schema,
  verticalFovDegrees: PositiveNumberSchema.max(120),
  nearMeters: PositiveNumberSchema,
  farMeters: PositiveNumberSchema,
  safeMargin: NonNegativeNumberSchema.max(0.4),
});

const LineageSchema = z
  .object({
    sourceProjectId: StableIdSchema,
    sourceRevisionId: StableIdSchema,
    rootProjectId: StableIdSchema,
    remixedAt: IsoDateSchema,
  })
  .nullable();

export const CoasterProjectV1Schema = z
  .object({
    schemaVersion: z.literal(COASTER_PROJECT_SCHEMA_VERSION),
    projectId: StableIdSchema,
    revisionId: StableIdSchema,
    coasterFamilyId: StableIdSchema,
    metadata: ProjectMetadataSchema,
    plot: PlotSchema,
    track: z.object({
      splineKind: z.literal('cubic_bspline'),
      degree: z.literal(3),
      closed: z.boolean(),
      nodes: z.array(TrackNodeSchema).max(2_000),
      sections: z.array(TrackSectionSchema).max(2_000),
    }),
    train: TrainSchema,
    appearance: AppearanceSchema,
    supports: z.object({
      mode: z.enum(['automatic', 'hidden']),
      suppressedSectionIds: z.array(StableIdSchema).max(2_000),
      manual: z.array(ManualSupportSchema).max(2_000),
    }),
    scenery: z.array(SceneryItemSchema).max(2_000),
    highlights: z.array(HighlightSchema).max(200),
    permissions: z.object({
      visibility: z.enum(['private', 'unlisted', 'public']),
      allowReadOnlyShare: z.boolean(),
      allowRemix: z.boolean(),
    }),
    validation: z.object({
      status: z.enum(['not_run', 'valid', 'warnings', 'blocked']),
      lastRunAt: IsoDateSchema.nullable(),
      validatorVersion: z.string().min(1).max(40),
      issues: z.array(ValidationIssueSchema).max(1_000),
    }),
    heroCamera: HeroCameraSchema,
    lineage: LineageSchema,
  })
  .superRefine((project, context) => {
    const requireUniqueIds = (label: string, values: readonly string[], path: PropertyKey[]) => {
      const seen = new Set<string>();
      values.forEach((id, index) => {
        if (seen.has(id)) {
          context.addIssue({
            code: 'custom',
            message: `Duplicate ${label} id: ${id}`,
            path: [...path, index, 'id'],
          });
        }
        seen.add(id);
      });
    };

    requireUniqueIds(
      'track node',
      project.track.nodes.map((node) => node.id),
      ['track', 'nodes'],
    );
    requireUniqueIds(
      'track section',
      project.track.sections.map((section) => section.id),
      ['track', 'sections'],
    );
    requireUniqueIds(
      'manual support',
      project.supports.manual.map((support) => support.id),
      ['supports', 'manual'],
    );
    requireUniqueIds(
      'scenery',
      project.scenery.map((item) => item.id),
      ['scenery'],
    );
    requireUniqueIds(
      'highlight',
      project.highlights.map((item) => item.id),
      ['highlights'],
    );
    requireUniqueIds(
      'validation issue',
      project.validation.issues.map((item) => item.id),
      ['validation', 'issues'],
    );

    const nodeIds = new Set(project.track.nodes.map((node) => node.id));
    const sectionIds = new Set(project.track.sections.map((section) => section.id));
    const supportIds = new Set(project.supports.manual.map((support) => support.id));
    const sceneryIds = new Set(project.scenery.map((item) => item.id));
    const highlightIds = new Set(project.highlights.map((item) => item.id));

    project.track.sections.forEach((section, index) => {
      if (!nodeIds.has(section.fromNodeId)) {
        context.addIssue({
          code: 'custom',
          message: `Unknown track node: ${section.fromNodeId}`,
          path: ['track', 'sections', index, 'fromNodeId'],
        });
      }
      if (!nodeIds.has(section.toNodeId)) {
        context.addIssue({
          code: 'custom',
          message: `Unknown track node: ${section.toNodeId}`,
          path: ['track', 'sections', index, 'toNodeId'],
        });
      }
      if (section.fromNodeId === section.toNodeId) {
        context.addIssue({
          code: 'custom',
          message: 'A section must connect two distinct nodes',
          path: ['track', 'sections', index],
        });
      }
    });

    project.supports.suppressedSectionIds.forEach((id, index) => {
      if (!sectionIds.has(id))
        context.addIssue({
          code: 'custom',
          message: `Unknown track section: ${id}`,
          path: ['supports', 'suppressedSectionIds', index],
        });
    });
    project.supports.manual.forEach((support, index) => {
      if (!sectionIds.has(support.sectionId))
        context.addIssue({
          code: 'custom',
          message: `Unknown track section: ${support.sectionId}`,
          path: ['supports', 'manual', index, 'sectionId'],
        });
    });
    project.highlights.forEach((highlight, index) => {
      if (!sectionIds.has(highlight.sectionId))
        context.addIssue({
          code: 'custom',
          message: `Unknown track section: ${highlight.sectionId}`,
          path: ['highlights', index, 'sectionId'],
        });
    });

    const referenceExists = (reference: z.infer<typeof ElementReferenceSchema>) => {
      switch (reference.kind) {
        case 'project':
          return reference.id === project.projectId;
        case 'track_node':
          return nodeIds.has(reference.id);
        case 'track_section':
          return sectionIds.has(reference.id);
        case 'support':
          return supportIds.has(reference.id);
        case 'scenery':
          return sceneryIds.has(reference.id);
        case 'highlight':
          return highlightIds.has(reference.id);
      }
    };
    project.validation.issues.forEach((issue, index) => {
      if (!referenceExists(issue.element))
        context.addIssue({
          code: 'custom',
          message: `Unknown ${issue.element.kind} reference: ${issue.element.id}`,
          path: ['validation', 'issues', index, 'element'],
        });
    });

    if (Date.parse(project.metadata.updatedAt) < Date.parse(project.metadata.createdAt)) {
      context.addIssue({
        code: 'custom',
        message: 'updatedAt cannot precede createdAt',
        path: ['metadata', 'updatedAt'],
      });
    }
    const cameraDelta = Math.hypot(
      project.heroCamera.position.x - project.heroCamera.target.x,
      project.heroCamera.position.y - project.heroCamera.target.y,
      project.heroCamera.position.z - project.heroCamera.target.z,
    );
    if (cameraDelta < 0.001)
      context.addIssue({
        code: 'custom',
        message: 'Hero camera position and target must differ',
        path: ['heroCamera', 'target'],
      });
    if (project.heroCamera.farMeters <= project.heroCamera.nearMeters)
      context.addIssue({
        code: 'custom',
        message: 'Hero camera far plane must exceed near plane',
        path: ['heroCamera', 'farMeters'],
      });
  });

export type StableId = z.infer<typeof StableIdSchema>;
export type Vec3 = z.infer<typeof Vec3Schema>;
export type TrackBehavior = z.infer<typeof TrackBehaviorSchema>;
export type TrackNode = z.infer<typeof TrackNodeSchema>;
export type TrackSection = z.infer<typeof TrackSectionSchema>;
export type CoasterProjectV1 = z.infer<typeof CoasterProjectV1Schema>;

export function parseCoasterProjectV1(value: unknown): CoasterProjectV1 {
  return CoasterProjectV1Schema.parse(value);
}

export function safeParseCoasterProjectV1(value: unknown) {
  return CoasterProjectV1Schema.safeParse(value);
}
